import React from 'react'
import { useLanguage } from '../../hooks/useLanguage'
import { useAccessibility } from '../../contexts/AccessibilityContext'
import { 
  GovLogo,
  PhoneIcon,
  EmailIcon,
  LocationIcon,
  TwitterIcon,
  FacebookIcon,
  InstagramIcon,
  ExternalLinkIcon
} from '../icons/Icons'

const Footer: React.FC = () => {
  const { t } = useLanguage()
  const { highContrast, textSizeMode, skipToMainContent, toggleHighContrast, setTextSizeMode } = useAccessibility()

  // Get current date for "Last updated" field
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const quickLinks = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    { label: 'Accessibility', href: '/accessibility' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'FAQ', href: '/faq' }
  ]

  const emergencyContacts = [
    { label: 'Police', number: '100' },
    { label: 'Fire', number: '101' },
    { label: 'Ambulance', number: '102' },
    { label: 'Disaster', number: '108' }
  ]

  const socialLinks = [
    { icon: TwitterIcon, href: '#', label: 'Twitter' },
    { icon: FacebookIcon, href: '#', label: 'Facebook' },
    { icon: InstagramIcon, href: '#', label: 'Instagram' }
  ]

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {/* Government Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-5">
              <div className="p-2 bg-blue-600 rounded-lg">
                <img src="/logo.png" alt="Kriyate Logo" className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold">{t('appName')}</h3>
                <p className="text-gray-300 text-sm md:text-base">Government of India</p>
              </div>
            </div>
            
            <p className="text-gray-300 mb-6 leading-relaxed text-sm md:text-base">
              {t('disclaimer')}
            </p>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <PhoneIcon className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm font-medium">24/7 Helpline</p>
                  <p className="text-blue-400 font-semibold text-base">1800-XXX-XXXX</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <EmailIcon className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm font-medium">Email Support</p>
                  <p className="text-blue-400 text-base">support@civicreporter.gov.in</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <LocationIcon className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Office Address</p>
                  <p className="text-gray-300 text-sm">
                    Ministry of Electronics & IT<br />
                    Electronics Niketan, CGO Complex<br />
                    New Delhi - 110003
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg md:text-xl font-semibold mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href} 
                    className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center space-x-2 group text-base"
                  >
                    <span>{link.label}</span>
                    <ExternalLinkIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Emergency Contacts */}
          <div>
            <h4 className="text-lg md:text-xl font-semibold mb-5">Emergency Numbers</h4>
            <ul className="space-y-3 mb-6">
              {emergencyContacts.map((contact, index) => (
                <li key={index} className="flex justify-between items-center">
                  <span className="text-gray-300 text-base">{contact.label}</span>
                  <a 
                    href={`tel:${contact.number}`}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-200"
                  >
                    {contact.number}
                  </a>
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div>
              <h5 className="text-sm font-semibold mb-4 text-gray-300">{t('followUs')}</h5>
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon
                  return (
                    <a
                      key={index}
                      href={social.href}
                      className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-lg flex items-center justify-center transition-colors duration-200 group"
                      aria-label={social.label}
                    >
                      <IconComponent className="w-5 h-5 text-gray-300 group-hover:text-white" />
                    </a>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-6 text-sm text-gray-400">
              <p>© 2025 Government of India. All rights reserved.</p>
              <div className="flex items-center space-x-3">
                <span>Last updated: {currentDate}</span>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>System Online</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 text-sm text-gray-400">
              <span>Developed with ❤️ for Digital India</span>
              <img 
                src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAzMiAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjgiIGZpbGw9IiNGRjkzMzMiLz4KPHJlY3QgeT0iOCIgd2lkdGg9IjMyIiBoZWlnaHQ9IjgiIGZpbGw9IiNGRkZGRkYiLz4KPHJlY3QgeT0iMTYiIHdpZHRoPSIzMiIgaGVpZ2h0PSI4IiBmaWxsPSIjMTM4ODA4Ii8+Cjwvc3ZnPgo="
                alt="Indian Flag"
                className="w-8 h-6 rounded-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Accessibility Banner */}
      <div className="bg-blue-600 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-3 md:space-y-0 md:space-x-6 text-center text-sm text-white">
            <button 
              onClick={skipToMainContent}
              className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded px-2 py-1"
            >
              Skip to Main Content
            </button>
            <span className="hidden md:block">•</span>
            <button 
              onClick={toggleHighContrast}
              className="hover:underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600 rounded px-2 py-1"
            >
              {highContrast ? 'Disable High Contrast Mode' : 'High Contrast Mode'}
            </button>
            <span className="hidden md:block">•</span>
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <span>Text Size:</span>
              <div className="flex space-x-2">
                <button 
                  onClick={() => setTextSizeMode('small')}
                  className={`px-3 py-1 rounded ${textSizeMode === 'small' ? 'bg-white text-blue-600 font-bold' : 'hover:bg-blue-500'}`}
                  aria-label="Small text"
                >
                  Small
                </button>
                <button 
                  onClick={() => setTextSizeMode('medium')}
                  className={`px-3 py-1 rounded ${textSizeMode === 'medium' ? 'bg-white text-blue-600 font-bold' : 'hover:bg-blue-500'}`}
                  aria-label="Medium text"
                >
                  Medium
                </button>
                <button 
                  onClick={() => setTextSizeMode('large')}
                  className={`px-3 py-1 rounded ${textSizeMode === 'large' ? 'bg-white text-blue-600 font-bold' : 'hover:bg-blue-500'}`}
                  aria-label="Large text"
                >
                  Large
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer